/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Clock, Send, Trash2, RefreshCw, Mail, KeyRound, Eye, EyeOff, Link as LinkIcon } from "lucide-react";
import { useBrutalConfirm } from "@/components/BrutalConfirm";

/**
 * iter74 — Pending Invites card.
 *
 * Surfaces all *unclaimed* invites (placeholder participants) for a Rivalry
 * in one spot inside the Players tab. Each row shows the invited email,
 * the assigned placeholder username (e.g. crewmate_3), a click-to-reveal
 * personal code, and the same Resend / Revoke actions exposed in the full
 * InviteByEmailCard. Once an invite is claimed it disappears from this
 * list (it shows up as a regular player row instead).
 */
export default function PendingInvitesCard({ leagueId, inviteCode, participants, isLocked, onChanged }) {
    // iter87.136 — `window.confirm` silently no-ops on mobile inside a
    // Radix Dialog (operator caught this AGAIN after iter87.135 — iter87.135
    // fixed the wrong card, `InviteByEmailCard`; the mobile Bento view
    // actually mounts this `PendingInvitesCard` inside the PlayersTab).
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);
    const [revealedCode, setRevealedCode] = useState(null);

    const load = useCallback(async () => {
        if (!leagueId) return;
        setLoading(true);
        try {
            const data = await api.listInviteEmails(leagueId);
            setInvites((data.invites || []).filter(inv => !inv.joined));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load invites");
        }
        setLoading(false);
    }, [leagueId]);

    useEffect(() => { load(); }, [load]);

    // Build a {email → placeholder participant} map so each invite row can
    // surface the assigned crewmate_N username + personal code.
    const placeholderByEmail = (participants || [])
        .filter(p => p?.is_placeholder && p?.email)
        .reduce((acc, p) => { acc[p.email.toLowerCase()] = p; return acc; }, {});

    const handleResend = async (inviteId, email) => {
        setBusy(inviteId);
        try {
            await api.resendInviteEmail(leagueId, inviteId);
            toast.success(`Login link re-sent to ${email}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Resend failed");
        }
        setBusy(null);
    };

    const handleRevoke = async (inviteId, email) => {
        const ok = await brutalConfirm({
            title: `Revoke invite to ${email}?`,
            body:
                "The placeholder slot will be deleted and the cap freed. " +
                "The invitee can no longer claim it with the code from their " +
                "email — the admin would need to send a fresh invite.",
            confirmLabel: "Revoke invite",
            cancelLabel: "Cancel",
            destructive: true,
        });
        if (!ok) return;
        setBusy(inviteId);
        try {
            await api.revokeInviteEmail(leagueId, inviteId);
            toast.success("Invite revoked");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Revoke failed");
        }
        setBusy(null);
    };

    // iter74 — Build a magic claim link the admin can drop into a text/Slack
    // DM for invitees who never received (or lost) the email. Lands on the
    // Join form with both code fields pre-filled; user only types their
    // username + clicks Join.
    const buildClaimLink = (personalCode) => {
        if (!inviteCode || !personalCode) return null;
        const origin = window.location.origin;
        const params = new URLSearchParams({ code: inviteCode, pcode: personalCode });
        return `${origin}/?${params.toString()}`;
    };

    const copyClaimLink = (personalCode, email) => {
        const link = buildClaimLink(personalCode);
        if (!link) {
            toast.error("Couldn't build claim link");
            return;
        }
        navigator.clipboard?.writeText(link);
        toast.success(`Claim link for ${email} copied`);
    };

    if (!loading && invites.length === 0) return null;

    return (
        <div className="brutal-card p-5 space-y-3" data-testid="pending-invites-card">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-cyber" />
                    <h2 className="font-display font-extrabold text-xl uppercase">Pending invites</h2>
                    <span className="pill bg-ink border-[#333] text-[10px] ml-1" data-testid="pending-invites-count">
                        {invites.length}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-40"
                    title="Refresh"
                    data-testid="pending-invites-refresh-btn"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Email invites that haven't been claimed yet. Each one reserves a slot with a
                placeholder username (e.g. <span className="font-mono text-cyber">crewmate_3</span>)
                and a personal code — the invitee picks their real username when they sign in.
            </p>

            <ul className="space-y-2" data-testid="pending-invites-list">
                {invites.map(inv => {
                    const ph = placeholderByEmail[inv.email];
                    const isBusyRow = busy === inv.id;
                    const codeRevealed = revealedCode === inv.id;
                    return (
                        <li
                            key={inv.id}
                            data-testid={`pending-invite-row-${inv.id}`}
                            className="brutal-card-light p-3 space-y-2"
                        >
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="flex-1 min-w-[160px]">
                                    <p className="font-mono text-sm text-zinc-200 truncate" data-testid={`pending-invite-email-${inv.id}`}>
                                        {inv.email}
                                    </p>
                                    <p className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                        <Clock className="w-3 h-3" />
                                        Sent {new Date(inv.sent_at).toLocaleString()}
                                        {inv.resent_count > 0 && (
                                            <span className="text-volt">· re-sent {inv.resent_count}×</span>
                                        )}
                                        {ph?.username && (
                                            <span className="pill bg-cyber/15 border-cyber/40 text-cyber text-[9px] font-bold">
                                                {ph.username}
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {ph?.personal_code && (
                                        <button
                                            type="button"
                                            data-testid={`pending-invite-claim-link-${inv.id}-btn`}
                                            onClick={() => copyClaimLink(ph.personal_code, inv.email)}
                                            disabled={isBusyRow}
                                            className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-30"
                                            title="Copy a one-click claim link (invite + personal code) to share via text / DM"
                                        >
                                            <LinkIcon className="w-3 h-3 text-cyber" /> Copy link
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`pending-invite-resend-${inv.id}-btn`}
                                        onClick={() => handleResend(inv.id, inv.email)}
                                        disabled={isLocked || isBusyRow}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-30"
                                        title="Re-send the original invite email"
                                    >
                                        {isBusyRow
                                            ? <RefreshCw className="w-3 h-3 animate-spin" />
                                            : <Send className="w-3 h-3 text-cyber" />} Resend
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`pending-invite-revoke-${inv.id}-btn`}
                                        onClick={() => handleRevoke(inv.id, inv.email)}
                                        disabled={isLocked || isBusyRow}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-30"
                                        title="Revoke invite + free the slot"
                                    >
                                        <Trash2 className="w-3 h-3 text-heat" />
                                    </button>
                                </div>
                            </div>
                            {ph?.personal_code && (
                                <div className="flex items-center gap-2 text-[10px] text-zinc-500 pt-1 border-t border-[#222]">
                                    <KeyRound className="w-3 h-3" />
                                    <span className="uppercase tracking-wider">Personal code</span>
                                    {codeRevealed ? (
                                        <>
                                            <span
                                                data-testid={`pending-invite-code-${inv.id}`}
                                                className="font-mono font-extrabold text-volt text-sm tracking-[0.12em] select-all"
                                            >
                                                {ph.personal_code}
                                            </span>
                                            <button
                                                type="button"
                                                data-testid={`pending-invite-code-hide-${inv.id}-btn`}
                                                onClick={() => setRevealedCode(null)}
                                                className="brutal-btn-ghost text-[10px] px-2 py-0.5"
                                            >
                                                <EyeOff className="w-3 h-3" />
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`pending-invite-code-copy-${inv.id}-btn`}
                                                onClick={() => {
                                                    navigator.clipboard?.writeText(ph.personal_code);
                                                    toast.success("Code copied");
                                                }}
                                                className="brutal-btn-ghost text-[10px] px-2 py-0.5"
                                            >
                                                Copy
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid={`pending-invite-code-show-${inv.id}-btn`}
                                            onClick={() => setRevealedCode(inv.id)}
                                            className="brutal-btn-ghost text-[10px] px-2 py-0.5"
                                        >
                                            <Eye className="w-3 h-3" /> Reveal
                                        </button>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
            {/* iter87.136 — Mount BrutalConfirm host so the revoke prompt
                renders on mobile inside the Admin Takeover. */}
            {confirmHost}
        </div>
    );
}
